<template>
  <div class="app-current-tariff">
    <slot v-if="!pendingTariff || pendingTariff.statusKey === 'SUS'" name="headingMyTariff"></slot>
    <slot v-else name="headingTariffChange"></slot>
    <slot name="txtBefore"></slot>
    <BookableDetailsWithActions
      v-if="bookedTariff"
      :details="bookedTariff.details"
      :actions="bookedTariff.actions"
      :info="bookedTariff.info"
      :content-theme="bookedTariff.tariffId"
      @click:details="showBookedTariffModal=true"
      @click:cancelTariff="cancelTariffAction(bookedTariff)"
      @click:changeTariff="scrollToTariffs"
    >
      <template v-for="slot in possibleChildSlotsFor('BookedTariff')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </BookableDetailsWithActions>
    <TariffInfoModal v-if="bookedTariff" :show.sync="showBookedTariffModal" :tariff="bookedTariff">
      <template v-for="slot in possibleChildSlotsFor('TariffModal')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </TariffInfoModal>
    <TariffInfoModal v-if="bookedTariff" :show.sync="showCanelTariffInfoModal" :tariff="bookedTariff">
      <template v-for="slot in possibleChildSlotsFor('TariffModalAnnual')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </TariffInfoModal>
    <CancelTariffModalWrapper
      v-if="bookedTariff"
      ref="modalAction"
      @refresh="refreshQuery"
    >
      <template v-for="slot in possibleChildSlotsFor('TariffModal')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </CancelTariffModalWrapper>
    <BookableDetailsWithActions
      v-if="pendingTariff"
      :details="pendingTariff.details"
      :actions="pendingTariff.actions"
      :info="pendingTariff.info"
      :content-theme="pendingTariff.tariffId"
      @click:details="showPendingTariffModal=true"
      @click:changeTariff="scrollToTariffs"
      @click:cancelTariff="cancelTariffAction(pendingTariff)"
      @click:reverseTariff="reverseTariffAction(pendingTariff)"
    >
      <template v-for="slot in possibleChildSlotsFor('PendingTariff')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </BookableDetailsWithActions>
    <ReverseTariffModalWrapper
      v-if="pendingTariff"
      @refresh="refreshQuery"
      ref="reverseModalAction">
      <template v-for="slot in possibleChildSlotsFor('ModalWrapper')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </ReverseTariffModalWrapper>
    <TariffInfoModal v-if="pendingTariff" :show.sync="showPendingTariffModal" :tariff="pendingTariff">
      <template v-for="slot in possibleChildSlotsFor('TariffModal')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </TariffInfoModal>
    <slot name="txtAfter"></slot>
  </div>
</template>

<script>
import BookableDetailsWithActions from './DetailsWithActions.vue'
import slotProxy from '@/mixins/slotProxy'
import { wrapQuery } from '@/helper/wrapGQLQuery'
import gql from 'graphql-tag'
import extraData from '@/mixins/extraData'
import joinTariffExtra from '@/helper/joinTariffExtras'
import { price } from '@/mixins/price'
import { durationUnit } from '@/mixins/durationUnit'
import { formatter } from '../../../../../../../config/calendar'
import TariffInfoModal from '@/components/process/generic/Tariff/generic/CurrentTariff/TariffInfoModal.vue'
import CancelTariffModalWrapper from './CancelTariffModalWrapper.vue'
import { mapActions } from 'vuex'
import ReverseTariffModalWrapper from './ReverseTariffModalWrapper.vue'
import debounce from 'lodash.debounce'

export default {
  name: 'CurrentTariff',
  components: { CancelTariffModalWrapper, BookableDetailsWithActions, TariffInfoModal, ReverseTariffModalWrapper },
  data () {
    return {
      tariffs: {
        bookedTariff: null,
        pendingTariff: null
      },
      showBookedTariffModal: false,
      showPendingTariffModal: false,
      showCanelTariffInfoModal: false
    }
  },
  // debounce function from https://stackoverflow.com/a/46187226
  updated: debounce(function () {
    this.$nextTick(() => {
      if (window.location.hash) {
        const el = document.querySelector(window.location.hash)
        if (el) {
          el.scrollIntoView()
        }
      }
    })
  }, 1000),
  methods: {
    ...mapActions('bookTariff', [
      'initiateCancelTariff'
    ]),
    scrollToTariffs () {
      if (this.tariffPageLink) {
        location.href = this.tariffPageLink
      } else {
        document.getElementById('tarif-section').scrollIntoView()
      }
    },
    compileTariff (tariff, isPending) {
      tariff = joinTariffExtra(tariff, this.extraData.tariffs)
      tariff.details = this.createDetails(tariff, isPending)
      tariff.info = this.createInfo(tariff, isPending)
      tariff.actions = [
        {
          type: 'details',
          text: 'Tarifdetails anzeigen'
        }
      ]
      // if bookableTariffs empty showing change tariff pointless
      if ((this.tariffs.pendingTariff == null || isPending) && this.tariffs.bookableTariffs?.bookableTariffs?.length) {
        tariff.actions.push(
          {
            type: 'changeTariff',
            text: 'Tarif wechseln'
          }
        )
      }
      if (tariff.cancelable) {
        if (isPending && tariff.isSuspendedActive === false) {
          tariff.actions.push(
            {
              type: 'reverseTariff',
              text: 'Stornieren'
            }
          )
        } else if (!this.tariffs.pendingTariff || (isPending && tariff.isSuspendedActive === true)) {
          if (this.isAnnualTariff) {
            tariff.actions.push(
              {
                type: 'cancelTariff',
                text: 'Tarifverlängerung'
              }
            )
          } else {
            tariff.actions.push(
              {
                type: 'cancelTariff',
                text: 'Tarif kündigen'
              }
            )
          }
        }
      }
      return tariff
    },
    cancelTariffAction (bookedTariff) {
      if (this.isAnnualTariff) {
        this.showCanelTariffInfoModal = true
      } else {
        this.initiateCancelTariff()
          .then((response) => {
            if (response.success === true) {
              this.$refs.modalAction.showCancelModal(response, bookedTariff.tariffId, bookedTariff)
            }
          })
      }
    },
    reverseTariffAction (pendingTariff) {
      this.$refs.reverseModalAction.showReverseModal(pendingTariff.tariffId, pendingTariff)
    },
    createDetails (tariff, isPending = false) {
      const flatTexts = []
      if (tariff.phoneFlat === 'all') {
        flatTexts.push(this.extraData.flatTexts.phone)
      }
      if (tariff.smsFlat === 'all') {
        flatTexts.push(this.extraData.flatTexts.sms)
      }
      let volumes = []
      if (tariff.tariffExtra !== undefined && tariff.tariffExtra.volumes instanceof Array) {
        volumes = tariff.tariffExtra.volumes
      }
      const priceDetail = {
        primary: price(tariff.basicFee || 0),
        secondary: tariff.runtime.amount === 0 ? null : '/' + tariff.runtime.amount + ' ' + durationUnit(tariff.runtime.unit.toUpperCase(), tariff.runtime.amount)
      }
      return [...flatTexts, ...volumes, priceDetail]
    },
    createInfo (tariff, isPending = false) {
      if (isPending && !tariff.isSuspendedActive) {
        tariff.statusKey = 'IDL'
      } else if (isPending && tariff.isSuspendedActive) {
        tariff.statusKey = 'SUSA'
      } else if (tariff.statusKey === 'CDL') {
        if (tariff.runtime.amount === 0) {
          tariff.statusKey = 'ACT'
        } else {
          tariff.statusKey = 'CON'
        }
      }
      let date = '-'
      const dateKey = this.extraData.infoTexts[tariff.statusKey].date
      if (dateKey && tariff[dateKey]) {
        date = formatter.dateFormatter.format(new Date(tariff[dateKey]))
      }
      let info = this.extraData.infoTexts[tariff.statusKey].text.replace('%DATE%', date)
      if (this.isAnnualTariff()) {
        info = 'Endet zum ' + date
      }
      const infoResult = {
        name: tariff.name,
        info: info
      }
      if (isPending) {
        infoResult.prepend = this.extraData.prependTexts?.pendingTariff ? this.extraData.prependTexts?.pendingTariff : 'Mein neuer Tarif'
      }
      return infoResult
    },
    refreshQuery () {
      this.$apollo.queries.tariffs.refetch()
    },
    isAnnualTariff () {
      return this.tariffs.bookedTariff.tariffExtra?.type === 'year'
    }
  },
  computed: {
    bookedTariff () {
      if (this.tariffs.bookedTariff) {
        return this.compileTariff(this.tariffs.bookedTariff, false)
      }
      return null
    },
    pendingTariff () {
      if (this.tariffs.pendingTariff) {
        return this.compileTariff(this.tariffs.pendingTariff, true)
      }
      return null
    }
  },
  mixins: [
    slotProxy(),
    extraData()
  ],
  props: {
    tariffPageLink: {
      type: String,
      default: null
    }
  },
  apollo: {
    tariffs: function () {
      return wrapQuery(
        gql`query bookedTariff {
         tariffs {
           bookableTariffs {
            bookableTariffs {
                tariffId
            }
           }
           bookedTariff {
           bandwidth {
            down
            up
           }
             tariffId
             name
             basicFee
             statusKey
             smsFlat
             cancelable
             tariffChangePossible
             isPendingTariff
             isSuspendedActive
             phoneFlat
             tariffState
             renewContractDate
             possibleChangingDate
             terminationDate
             changeTariffDate
             runtime {
               amount
               unit
             }
             volumes {
               amount
               formattedUnit
               type
               unit
             }
           }
           pendingTariff {
             tariffId
             name
             tariffState
             basicFee
             tariffChangePossible
             isPendingTariff
             isSuspendedActive
             cancelable
             statusKey
             smsFlat
             phoneFlat
             phoneFlat
             tariffState
             renewContractDate
             possibleChangingDate
             terminationDate
             changeTariffDate
             runtime {
               amount
               unit
             }
             volumes {
               amount
               formattedUnit
               type
               unit
             }
           }
         }
        }`, this, 'tariffs')
    }
  }
}
</script>
